/** Smart tooltip placement — avoids overlapping the highlighted target. */

function clamp(n, min, max) {
  // Guard against inverted ranges (happens when the tooltip is taller/wider
  // than the viewport) — without this, Math.max(min, n) can exceed max and
  // the caller ends up with a top/left that pushes content off-screen.
  if (max < min) return min;
  return Math.min(max, Math.max(min, n));
}

const VIEWPORT_MARGIN = 12;

/**
 * Single source of truth for how tall the tooltip is allowed to be.
 * Used both to size the tooltip (CSS maxHeight) and to work out where it
 * can be placed — keeping these in sync is what prevents the footer
 * (Back/Next buttons) from ending up outside the viewport.
 */
export function getMaxTooltipHeight() {
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  return Math.max(240, vh - 24);
}

/**
 * Centered fallback position (used while we don't yet have a target rect
 * to anchor against — e.g. right after navigating to a new page). Always
 * clamped fully inside the viewport so the footer buttons stay reachable.
 */
export function resolveCenteredPosition(tooltipW, tooltipH) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const top = clamp(
    (vh - tooltipH) / 2,
    VIEWPORT_MARGIN,
    vh - tooltipH - VIEWPORT_MARGIN,
  );
  const left = clamp(
    (vw - tooltipW) / 2,
    VIEWPORT_MARGIN,
    vw - tooltipW - VIEWPORT_MARGIN,
  );
  return { top, left, docked: false, placement: "center" };
}

function highlightBox(rect, pad = 8) {
  return {
    top: rect.top - pad,
    left: rect.left - pad,
    right: rect.left + rect.width + pad,
    bottom: rect.top + rect.height + pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

function tooltipBox(top, left, w, h) {
  return { top, left, right: left + w, bottom: top + h, width: w, height: h };
}

function overlaps(a, b, gap = 12) {
  return !(
    a.right + gap < b.left ||
    a.left - gap > b.right ||
    a.bottom + gap < b.top ||
    a.top - gap > b.bottom
  );
}

function clampToViewport(top, left, w, h, margin = 12) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    top: clamp(top, margin, vh - h - margin),
    left: clamp(left, margin, vw - w - margin),
  };
}

/**
 * Keep the tour card fully on screen and return the maxHeight the card
 * may use from that top offset so the footer (Back/Next) is never clipped.
 */
export function ensureTooltipInViewport(top, left, width, height, margin = 12) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const maxH = Math.min(getMaxTooltipHeight(), vh - margin * 2);
  const w = Math.min(width || 280, vw - margin * 2);
  const h = Math.min(height || 220, maxH);
  let t = clamp(top, margin, Math.max(margin, vh - h - margin));
  let l = clamp(left, margin, Math.max(margin, vw - w - margin));
  if (t + h > vh - margin) t = Math.max(margin, vh - h - margin);
  if (l + w > vw - margin) l = Math.max(margin, vw - w - margin);
  const available = Math.max(200, vh - t - margin);
  return {
    top: t,
    left: l,
    maxHeight: Math.min(maxH, available),
  };
}

function isLargeHighlight(h, vw, vh) {
  // Only full-height sidebars / almost-full-page panels. Wide-but-short
  // cards (ROPA cover info rows) must NOT count as large — that was pinning
  // every cover tooltip to the same docked corner.
  return h.height > vh * 0.5 || (h.width > vw * 0.75 && h.height > vh * 0.42);
}

/**
 * Tries to dock the tooltip into whichever free strip of viewport space
 * (above / below / left / right of the highlight) actually has room for
 * it, guaranteeing zero overlap with the highlighted target whenever any
 * strip is big enough. This is what the old corner-only logic was missing:
 * for large targets (a tall answer card, a full-height sidebar) every
 * fixed corner can overlap the highlight, so the old code fell back to
 * "least-bad corner" and pinned the tooltip on top of the very content
 * it's explaining.
 */
function resolveStripPosition(h, tooltipW, tooltipH, margin, gap = 12) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const strips = [
    {
      side: "right",
      available: vw - h.right - gap - margin,
      build: (w, height) => ({
        top: clamp(
          h.top + h.height / 2 - height / 2,
          margin,
          Math.max(margin, vh - height - margin),
        ),
        left: vw - w - margin,
      }),
    },
    {
      side: "left",
      available: h.left - gap - margin,
      build: (w, height) => ({
        top: clamp(
          h.top + h.height / 2 - height / 2,
          margin,
          Math.max(margin, vh - height - margin),
        ),
        left: margin,
      }),
    },
    {
      side: "bottom",
      available: vh - h.bottom - gap - margin,
      build: (w, height) => ({
        top: vh - height - margin,
        left: clamp(
          h.left + h.width / 2 - w / 2,
          margin,
          Math.max(margin, vw - w - margin),
        ),
      }),
    },
    {
      side: "top",
      available: h.top - gap - margin,
      build: (w, height) => ({
        top: margin,
        left: clamp(
          h.left + h.width / 2 - w / 2,
          margin,
          Math.max(margin, vw - w - margin),
        ),
      }),
    },
  ];

  // Prefer the strip with the most room, but only ones that can actually
  // fit the tooltip's minimum readable size (don't squeeze it down to an
  // unreadable sliver).
  const MIN_USABLE_W = 220;
  const MIN_USABLE_H = 160;

  const viable = strips
    .filter((s) => (s.side === "right" || s.side === "left"
      ? s.available >= MIN_USABLE_W
      : s.available >= MIN_USABLE_H))
    .sort((a, b) => b.available - a.available);

  if (!viable.length) return null;

  const best = viable[0];
  const isHorizontal = best.side === "right" || best.side === "left";
  const w = isHorizontal
    ? Math.min(tooltipW, best.available)
    : Math.min(tooltipW, vw - margin * 2);
  const height = isHorizontal
    ? Math.min(tooltipH, vh - margin * 2)
    : Math.min(tooltipH, best.available);
  const pos = best.build(w, height);

  return {
    top: pos.top,
    left: pos.left,
    docked: true,
    placement: "docked",
  };
}

function resolveDockedPosition(h, tooltipW, tooltipH, margin = 12) {
  const stripPos = resolveStripPosition(h, tooltipW, tooltipH, margin);
  if (stripPos) return stripPos;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const dockW = Math.min(tooltipW, vw - margin * 2);
  const dockH = Math.min(tooltipH, vh - margin * 2);
  const targetCenterX = h.left + h.width / 2;
  const targetCenterY = h.top + h.height / 2;
  const preferRight = targetCenterX < vw * 0.5;
  const preferTop = targetCenterY > vh * 0.45;

  const corners = [
    preferRight && preferTop
      ? { top: margin, left: vw - dockW - margin }
      : null,
    preferRight && !preferTop
      ? { top: vh - dockH - margin, left: vw - dockW - margin }
      : null,
    !preferRight && preferTop
      ? { top: margin, left: margin }
      : null,
    !preferRight && !preferTop
      ? { top: vh - dockH - margin, left: margin }
      : null,
    { top: margin, left: vw - dockW - margin },
    { top: vh - dockH - margin, left: vw - dockW - margin },
    { top: margin, left: margin },
    { top: vh - dockH - margin, left: margin },
  ].filter(Boolean);

  let chosen = corners.find((corner) => {
    const tBox = tooltipBox(corner.top, corner.left, dockW, dockH);
    return !overlaps(h, tBox, 8);
  });

  if (!chosen) {
    chosen = corners.reduce(
      (best, corner) => {
        const tBox = tooltipBox(corner.top, corner.left, dockW, dockH);
        const dist =
          Math.abs(tBox.left + dockW / 2 - targetCenterX) +
          Math.abs(tBox.top + dockH / 2 - targetCenterY);
        return dist > best.dist ? { corner, dist } : best;
      },
      { corner: corners[0], dist: -Infinity },
    ).corner;
  }

  return {
    top: chosen.top,
    left: chosen.left,
    docked: true,
    placement: "docked",
  };
}

/**
 * @returns {{ top: number, left: number, docked: boolean, placement: string }}
 */
export function resolveTooltipPosition(
  highlight,
  tooltipW,
  tooltipH,
  preferred = "bottom",
  { forceDock = false } = {},
) {
  const h = highlightBox(highlight);
  const gap = 16;
  const margin = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (forceDock || isLargeHighlight(h, vw, vh)) {
    return resolveDockedPosition(h, tooltipW, tooltipH, margin);
  }

  const order = [preferred, "bottom", "top", "right", "left"].filter(
    (v, i, arr) => arr.indexOf(v) === i,
  );

  const candidates = order.map((placement) => {
    let top;
    let left;
    if (placement === "top") {
      top = h.top - tooltipH - gap;
      left = h.left + h.width / 2 - tooltipW / 2;
    } else if (placement === "left") {
      top = h.top;
      left = h.left - tooltipW - gap;
    } else if (placement === "right") {
      top = h.top;
      left = h.right + gap;
    } else {
      top = h.bottom + gap;
      left = h.left;
    }
    const clamped = clampToViewport(top, left, tooltipW, tooltipH, margin);
    const tBox = tooltipBox(clamped.top, clamped.left, tooltipW, tooltipH);
    const overlap = overlaps(h, tBox);
    const centerDist =
      Math.abs(tBox.left + tooltipW / 2 - (h.left + h.width / 2)) +
      Math.abs(tBox.top + tooltipH / 2 - (h.top + h.height / 2));
    return {
      placement,
      ...clamped,
      overlap,
      score:
        (overlap ? 10000 : 0) + centerDist + (placement === preferred ? 0 : 50),
    };
  });

  candidates.sort((a, b) => a.score - b.score);
  const best = candidates.find((c) => !c.overlap) || candidates[0];

  if (best && !best.overlap) {
    const fitted = ensureTooltipInViewport(
      best.top,
      best.left,
      tooltipW,
      tooltipH,
      margin,
    );
    return {
      top: fitted.top,
      left: fitted.left,
      docked: false,
      placement: best.placement,
    };
  }

  return resolveDockedPosition(h, tooltipW, tooltipH, margin);
}

export const COMPACT_TYPES = new Set(["nav", "button", "stat", "field", "tab"]);
export const COMPACT_MAX_W = 280;
export const STANDARD_MAX_W = 380;

export function resolveStepSize(step) {
  if (step?.size === "compact" || step?.size === "standard") return step.size;
  if (COMPACT_TYPES.has(step?.elementType)) return "compact";
  return "standard";
}